import React, { useEffect, useState } from 'react';
import DynamicLayout from 'src/components/Layout/DynamicLayout';
import { ProgressReportView } from 'src/components/ProgressReportView';
import { ChildViewReportSidebar } from 'src/components/Sidebars/User/ChildViewReportSidebar';
import useProgressReport from 'src/hooks/useProgressReport';
import { ProgressSkeleton } from 'src/components/Skeletons/ProgressReportSkeleton';
import { useAppDispatch, useAppSelector } from 'src/redux/redux-store/hooks';
import { Badge, Select } from '@mantine/core';
import { UserRole } from 'src/enums/User';
import { userService } from 'src/services/services';
import { ResponseKind } from 'src/services/services';
import { setActiveSubject as setChildActiveSubject } from 'src/redux/redux-store/children/childrenReducer';
import { setActiveSubject as setChatActiveSubject } from 'src/redux/redux-store/chat/chatReducer';
import { Subject } from 'src/interfaces/Children';

// ---------------- Dashboard ----------------
export const ReportsScreen: React.FC = () => {
  const { progressReportData, loading, error } = useProgressReport();
  const dispatch = useAppDispatch();

  // Redux Selectors
  const { selectedChild, activeSubject: childActiveSubject, children, subjects: parentViewSubjects } =
    useAppSelector((state) => state.child);
  const { activeSubject: chatActiveSubject } = useAppSelector(
    (state) => state.chat
  );
  const userRole = useAppSelector((state) => state.user.role);

  // Local State
  const [subjects, setSubjects] = useState<string[]>([]);
  const [currentSubject, setCurrentSubject] = useState<string | null>(null);

  // Determine active subject based on role
  useEffect(() => {
    if (userRole === UserRole.user) {
      setCurrentSubject(chatActiveSubject);
    } else {
      setCurrentSubject(childActiveSubject);
    }
  }, [userRole, chatActiveSubject, childActiveSubject]);

  // Fetch subjects logic
  useEffect(() => {
    const fetchSubjects = async () => {
      // 1. Parent View logic
      if (userRole !== UserRole.user) {
        // useChildrenData hook already fetches subjects into redux state.child.subjects
        // We verify the data type (string or object)
        if (parentViewSubjects) {
          const subs = parentViewSubjects.map((s: any) =>
            typeof s === 'string' ? s : s.name
          );
          setSubjects(subs);
        } else {
          setSubjects([]);
        }
      }
      // 2. Student View logic
      else if (userRole === UserRole.user) {
        // Fetch all subjects for the student
        try {
          const res = await userService.getAllSubjects();
          if (res.kind === ResponseKind.OK && res.data) {
            // res.data is Subject[] based on user.api.ts
            setSubjects((res.data as any[]).map((s: any) => s.name));
          }
        } catch (err) {
          console.error('Failed to fetch subjects for user:', err);
        }
      }
    };

    fetchSubjects();
  }, [userRole, selectedChild, children]);

  const handleSubjectChange = (val: string | null) => {
    if (!val) return;
    if (userRole === UserRole.user) {
      dispatch(setChatActiveSubject(val));
    } else {
      dispatch(setChildActiveSubject(val));
    }
  };

  const subjectSelect = (
    <Select
      placeholder="Select Subject"
      data={subjects}
      value={currentSubject}
      onChange={handleSubjectChange}
      searchable
      clearable={false}
      className="w-36"
      size="xs"
      disabled={subjects.length === 0}
    />
  );

  return (
    <DynamicLayout
      key={0}
      heading={
        <div className="flex items-center gap-4">
          <span className="text-xl font-bold">Progress Report</span>
          {selectedChild && (
            <Badge size="lg" variant="outline">
              Child: {selectedChild}
            </Badge>
          )}
          {subjectSelect}
        </div>
      }
      rightContent={subjectSelect}
      SidebarComponent={ChildViewReportSidebar}
    >
      {loading ? (
        <ProgressSkeleton />
      ) : error ? (
        <p className="text-red-500 p-4">{error}</p>
      ) : (
        <ProgressReportView data={progressReportData} />
      )}
    </DynamicLayout>
  );
};
